import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

export type BuilderSegmentOption = {
  value: string
  label: string
  /** Extra class on the segment button (e.g. parts-auth-form__segment--replaced_aftermarket). */
  segmentClass?: string
}

type Props = {
  value: string
  options: readonly BuilderSegmentOption[]
  onChange: (value: string) => void
  ariaLabel: string
  /** e.g. builder-defect-segmented for compact defect inspector pills */
  className?: string
}

/** Pill cap shape for sliding thumb (first / middle / last segment). */
function thumbCapIndex(activeIndex: number, count: number): number {
  if (count <= 1 || activeIndex <= 0) return 0
  if (activeIndex >= count - 1) return 3
  if (count === 3 && activeIndex === 1) return 1
  return activeIndex === 1 ? 1 : 2
}

export default function BuilderSegmentedControl({
  value,
  options,
  onChange,
  ariaLabel,
  className = '',
}: Props) {
  const activeIndex = Math.max(
    0,
    options.findIndex((o) => o.value === value),
  )
  const thumbCap = thumbCapIndex(activeIndex, options.length)
  const groupRef = useRef<HTMLDivElement>(null)
  const segmentRefs = useRef<(HTMLButtonElement | null)[]>([])
  const [thumbRect, setThumbRect] = useState({ width: 0, left: 0 })

  const syncThumb = useCallback(() => {
    const group = groupRef.current
    const btn = segmentRefs.current[activeIndex]
    if (!group || !btn) return
    const groupBox = group.getBoundingClientRect()
    const btnBox = btn.getBoundingClientRect()
    setThumbRect({
      width: btnBox.width,
      left: btnBox.left - groupBox.left,
    })
  }, [activeIndex])

  useLayoutEffect(() => {
    syncThumb()
  }, [syncThumb, value, options])

  useEffect(() => {
    const group = groupRef.current
    if (!group || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => syncThumb())
    ro.observe(group)
    window.addEventListener('resize', syncThumb)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', syncThumb)
    }
  }, [syncThumb])

  return (
    <div
      ref={groupRef}
      className={`parts-auth-form__segmented${className ? ` ${className}` : ''}`}
      role="radiogroup"
      aria-label={ariaLabel}
      data-segment-active={activeIndex}
      style={{ ['--builder-segment-cols' as string]: String(options.length) }}
    >
      <span
        className={`parts-auth-form__segment-thumb parts-auth-form__segment-thumb--pos-${thumbCap}`}
        aria-hidden
        style={{
          width: thumbRect.width > 0 ? `${thumbRect.width}px` : undefined,
          transform: `translateX(${thumbRect.left}px)`,
        }}
      />
      {options.map((opt, index) => {
        const on = value === opt.value
        return (
          <button
            key={opt.value}
            ref={(el) => {
              segmentRefs.current[index] = el
            }}
            type="button"
            role="radio"
            aria-checked={on}
            className={`parts-auth-form__segment${opt.segmentClass ? ` ${opt.segmentClass}` : ''}${
              on ? ' parts-auth-form__segment--on' : ''
            }`}
            onClick={() => onChange(opt.value)}
          >
            <span className="parts-auth-form__segment-text">{opt.label}</span>
          </button>
        )
      })}
    </div>
  )
}
